import {
  Box,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SpaceProps,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  TypographyProps,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { colors } from '~/packages/common-frontend/colors';
import {
  ColumnInfo,
  CsvTape,
  XlsxTape,
} from '~/packages/manualDataTapeUploads/types';

// Constants
const spaceBetween = '20px';

// Fragments
type BadgedTextProps = TypographyProps &
  SpaceProps & { children: React.ReactNode };
const BadgedText = ({
  children,
  fontSize,
  fontWeight,
  padding,
}: BadgedTextProps) => (
  <Text
    display="inline-block"
    bg={colors.gray[200]}
    fontSize={fontSize ?? 'sm'}
    fontWeight={fontWeight ?? 'semibold'}
    mt="5px"
    mb="5px"
    padding={padding ?? '5px 8px'}
    borderRadius="8px"
    overflowWrap="anywhere"
  >
    {children}
  </Text>
);

const SmallBadgedText = ({ children }: { children: React.ReactNode }) => (
  <BadgedText fontSize="xs" fontWeight="medium" padding="3px 8px">
    {children}
  </BadgedText>
);

// Field names can be very long, so set a maxWidth + overflowWrap=anywhere
type FieldNameCellProps = {
  name: string;
  type: string;
  isRequired: boolean;
};
const FieldNameCell = ({ name, type, isRequired }: FieldNameCellProps) => (
  <Td maxWidth="40%">
    <Text>
      <BadgedText>{name}</BadgedText>
    </Text>
    <Text fontSize="sm">Type: {type}</Text>
    {!isRequired && (
      <Text>
        <SmallBadgedText>Optional</SmallBadgedText>
      </Text>
    )}
  </Td>
);

type DescriptionCellProps = {
  description: string;
  defaultValue: Util.Maybe<string>;
};
const DescriptionCell = ({
  description,
  defaultValue,
}: DescriptionCellProps) => (
  <Td>
    <Text>{description}</Text>
    {!_.isNil(defaultValue) && (
      <Text mt="20px">
        Default if unspecified:{' '}
        <SmallBadgedText>{defaultValue}</SmallBadgedText>
      </Text>
    )}
  </Td>
);

type ExamplesCellProps = { examples: string[] };
const ExamplesCell = ({ examples }: ExamplesCellProps) => (
  <Td>
    {examples.map((example) => (
      <Box key={uuidv4()}>
        <SmallBadgedText>{example}</SmallBadgedText>
      </Box>
    ))}
  </Td>
);

// Used for:
// - CSV tapes
// - Tapes extracted from an Excel sheet
type TapeColumnsTableProps = { columns: ColumnInfo[] };
const TapeColumnsTable = ({ columns }: TapeColumnsTableProps) => (
  <Table mb={spaceBetween}>
    <Thead>
      <Tr>
        <Th>Field Name</Th>
        <Th>Description</Th>
        <Th>Sample Data</Th>
      </Tr>
    </Thead>
    <Tbody>
      {columns.map(
        ({ name, required, type, description, examples, defaultValue }) => (
          <Tr key={name}>
            <FieldNameCell name={name} type={type} isRequired={required} />
            <DescriptionCell
              description={description}
              defaultValue={defaultValue}
            />
            <ExamplesCell examples={examples} />
          </Tr>
        ),
      )}
    </Tbody>
  </Table>
);

export type PickedCsvTape = Pick<CsvTape, 'fileType' | 'columns'>;
export type PickedXlsxTape = Pick<
  XlsxTape,
  'fileType' | 'valueFieldGroups' | 'tapeFieldGroups'
>;
type PickedDataTape = PickedCsvTape | PickedXlsxTape;

type CsvTapeTableProps = {
  csvTape: PickedCsvTape;
};
export const CsvTapeTable = ({ csvTape: { columns } }: CsvTapeTableProps) => (
  <>
    <TapeColumnsTable columns={columns} />
  </>
);

type XlsxTapeTableProps = {
  xlsxTape: PickedXlsxTape;
};
export const XlsxTapeTable = ({
  xlsxTape: { valueFieldGroups, tapeFieldGroups },
}: XlsxTapeTableProps) => (
  <>
    {valueFieldGroups.map((fieldGroup) => (
      <Box key={fieldGroup.dataTapeSlug}>
        <Heading fontSize="14" mb={spaceBetween}>
          {commonUtils.toTitleCase(fieldGroup.dataTapeSlug)}
        </Heading>
        <Table mb={spaceBetween}>
          <Thead>
            <Tr>
              <Th>Location</Th>
              <Th>Field Name</Th>
              <Th>Description</Th>
              <Th>Sample Data</Th>
            </Tr>
          </Thead>
          <Tbody>
            {fieldGroup.columns.map(
              ({
                sheetName,
                cellAddress,
                name,
                required,
                type,
                description,
                examples,
                defaultValue,
              }) => (
                <Tr key={name}>
                  <Td>
                    <Text>{sheetName}</Text>
                    <Text>{cellAddress}</Text>
                  </Td>
                  <FieldNameCell
                    name={name}
                    type={type}
                    isRequired={required}
                  />
                  <DescriptionCell
                    description={description}
                    defaultValue={defaultValue}
                  />
                  <ExamplesCell examples={examples} />
                </Tr>
              ),
            )}
          </Tbody>
        </Table>
      </Box>
    ))}
    {tapeFieldGroups.map(
      ({ dataTapeSlug, sheetName, headerRange, columns }) => (
        <Box key={dataTapeSlug}>
          <Heading fontSize="14" mb={spaceBetween}>
            {commonUtils.toTitleCase(dataTapeSlug)} (Sheet: {sheetName}
            {headerRange && <>, Header range: {headerRange}</>})
          </Heading>
          <TapeColumnsTable columns={columns} />
        </Box>
      ),
    )}
  </>
);

type DataTapeFormatModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dataTape: PickedDataTape;
};
const isCsvTape = (tape: PickedDataTape): tape is PickedCsvTape =>
  'columns' in tape;

export const DataTapeFormatModal = ({
  isOpen,
  onClose,
  dataTape,
}: DataTapeFormatModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size="lg">
    <ModalOverlay />
    <ModalContent padding="20px" width="auto" minW="5xl">
      <ModalCloseButton />
      <ModalBody>
        <Heading fontSize="16" mb={spaceBetween}>
          Data Tape Format
        </Heading>
        {isCsvTape(dataTape) && <CsvTapeTable csvTape={dataTape} />}
        {!isCsvTape(dataTape) && <XlsxTapeTable xlsxTape={dataTape} />}
      </ModalBody>
    </ModalContent>
  </Modal>
);
